import React from 'react';
import type { StrictModifiers } from '@popperjs/core';
import Popper, { Placement } from '../../../ui/Popper';
import { CheckIcon } from '../../assets/icons';
import * as S from './DropdownStyled';

export type DropdownItem = {
  label: string;
  value: string | number;
};

export const dropdownPanelOffsetModifier: StrictModifiers = {
  name: 'offset',
  options: { offset: [0, 8] },
};

type DropdownListPanelProps = {
  open: boolean;
  targetRef?: HTMLElement;
  width: number;
  options: readonly DropdownItem[];
  panelTestId?: string;
  optionTestIdPrefix?: string;
  selectedValue?: string | number;
  zIndex?: number;
  modifiers?: StrictModifiers[];
  emptyContent?: React.ReactNode;
  showSelectedCheck?: boolean;
  optionBold?: boolean;
  onSelect: (option: DropdownItem) => void;
  onOptionMouseDown?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

const DropdownListPanel = ({
  open,
  targetRef,
  width,
  options,
  panelTestId,
  optionTestIdPrefix,
  selectedValue,
  zIndex,
  modifiers = [],
  emptyContent,
  showSelectedCheck = false,
  optionBold = true,
  onSelect,
  onOptionMouseDown,
}: DropdownListPanelProps) => (
  <Popper
    open={open}
    targetRef={targetRef}
    placement={Placement.BottomStart}
    style={{ width, ...(zIndex !== undefined ? { zIndex } : {}) }}
    modifiers={[dropdownPanelOffsetModifier, ...modifiers]}
  >
    <S.DropdownPanel role="listbox" data-dropdown-panel data-testid={panelTestId} $width={width}>
      {options.length > 0
        ? options.map((option) => {
            const selected = selectedValue != null && option.value === selectedValue;

            return (
              <S.DropdownOption
                key={String(option.value)}
                type="button"
                role="option"
                data-testid={optionTestIdPrefix ? `${optionTestIdPrefix}-${String(option.value)}` : undefined}
                aria-selected={selected}
                $selected={selected}
                $bold={optionBold}
                onMouseDown={onOptionMouseDown}
                onClick={() => onSelect(option)}
              >
                <span>{option.label}</span>
                {showSelectedCheck && selected && <CheckIcon />}
              </S.DropdownOption>
            );
          })
        : emptyContent}
    </S.DropdownPanel>
  </Popper>
);

export default DropdownListPanel;
